/** @module The card finder's training: whether it runs, what it last learned, and its log. */
import { useTranslation } from "react-i18next";

import { bytes } from "@/lib/format";
import { LogView } from "@/features/admin/log-view";
import { useTraining, useTrainingControl } from "@/features/admin/use-admin";

function percent(value: unknown): string {
  return typeof value === "number" ? `${(value * 100).toFixed(1)} %` : "–";
}

/** The run reports are free-form JSON; only the simple values are worth printing. */
function text(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "–";
}

export function TrainingPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const training = useTraining();
  const control = useTrainingControl();

  if (training.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (training.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const data = training.data;
  const last = data.last_run ?? {};
  const state = data.running
    ? "running"
    : data.stopping
      ? "stopping"
      : data.requested
        ? "requested"
        : "idle";

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 panel panel-lg">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="t-h3">{t("admin.training.state")}</h3>
          <span
            style={{
              color: data.running ? "var(--accent)" : data.requested ? "var(--warn)" : undefined,
            }}
          >
            {t(`admin.training.states.${state}`)}
          </span>
        </div>

        {data.started_at ? (
          <p className="t-sm t-muted">
            {t("admin.training.since", {
              time: new Date(data.started_at).toLocaleString(locale),
            })}
          </p>
        ) : null}

        <p className="t-sm t-muted">{t("admin.training.handover")}</p>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={control.isPending || data.running || data.requested}
            onClick={() => {
              control.mutate("start");
            }}
            className="btn btn-primary"
          >
            {t("admin.training.start")}
          </button>
          <button
            type="button"
            disabled={control.isPending || (!data.running && !data.requested)}
            onClick={() => {
              control.mutate("stop");
            }}
            className="btn btn-secondary disabled:opacity-50"
          >
            {t("admin.training.stop")}
          </button>
        </div>
        {control.isError ? (
          <p role="alert" className="t-sm loss">
            {control.error instanceof Error ? control.error.message : t("common.saveError")}
          </p>
        ) : null}
      </section>

      <section className="flex flex-col gap-2 panel panel-lg">
        <h3 className="t-h3">{t("admin.training.model")}</h3>
        <dl className="grid gap-x-6 gap-y-1 text-[length:var(--text-sm)] sm:grid-cols-2">
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.training.overlap")}</dt>
            <dd className="num">{percent(last.best_iou)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.training.photos")}</dt>
            <dd className="num">{text(last.labelled_from_confirmations)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.training.minutes")}</dt>
            <dd className="num">{text(last.minutes)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.training.size")}</dt>
            <dd className="num">{bytes(data.model_bytes, locale)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.training.installed")}</dt>
            <dd className="num">
              {data.model_installed_at
                ? new Date(data.model_installed_at).toLocaleString(locale)
                : "–"}
            </dd>
          </div>
        </dl>
      </section>

      {data.history.length > 0 ? (
        <section className="panel panel-lg">
          <h3 className="mb-2 t-h3">{t("admin.training.history")}</h3>
          <table className="w-full text-[length:var(--text-sm)]">
            <thead>
              <tr className="text-left text-(--text-muted)">
                <th className="py-1">{t("admin.training.started")}</th>
                <th className="py-1 text-right">{t("admin.training.photos")}</th>
                <th className="py-1 text-right">{t("admin.training.overlap")}</th>
                <th className="py-1 text-right">{t("admin.training.installed")}</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((row, index) => (
                <tr
                  key={`${text(row.started)}-${String(index)}`}
                  className="border-t border-(--border)"
                >
                  <td className="py-1">
                    {typeof row.started === "string"
                      ? new Date(row.started).toLocaleString(locale)
                      : "–"}
                  </td>
                  <td className="num py-1 text-right">{text(row.labelled_from_confirmations)}</td>
                  <td className="num py-1 text-right">{percent(row.best_iou)}</td>
                  <td className="py-1 text-right">
                    {row.installed ? t("admin.training.yes") : t("admin.training.no")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <h3 className="t-h3">{t("admin.training.log")}</h3>
        <LogView lines={data.log} empty={t("admin.training.noLog")} />
      </section>
    </div>
  );
}
